import {
  Component,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  Inject,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Permissions } from 'app/shared/permissions/permissions';
import { PRIMECONTROL_BUTTONS } from 'app/shared/buttons/buttons';
import { PC_DIALOG, PcDialogRef } from 'app/shared/dialog/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { InvitationService } from 'app/core/services/invitation/invitation.service';
import { NotificationService } from 'app/core/services/notification/notification.service';
import { RoleService } from 'app/core/services/role/role.service';
import { Button } from 'app/shared/atoms/button/button';
import { InputField } from 'app/shared/atoms/input/input';
import { Select } from 'app/shared/atoms/select/select';
import { ModalBody } from 'app/shared/molecules/modal/modal-body';
import { ModalFooter } from 'app/shared/molecules/modal/modal-footer';

/** Something after the @ and a dot in the domain: "ana@empresa" is refused. */
const EMAIL_WITH_DOMAIN_DOT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Claims that allow inviting. The API checks `InvitationsSend`; the webapp's
 * `Instances.SendInvitation` (what home gates on) has no counterpart in the
 * API's Permissions.cs, so either one is accepted.
 */
const INVITE_CLAIMS = ['InvitationsSend', Permissions.Instances.SendInvitation];

/** The address as it is sent: no surrounding spaces, lower case. */
export function normalizeInvitationEmail(email: string): string {
  return (email ?? '').trim().toLowerCase();
}

/** Valid email (Angular's rule) with a dot in the domain, judged on the trimmed value. */
export function invitationEmailValidator(control: AbstractControl): ValidationErrors | null {
  const email = normalizeInvitationEmail(control.value);
  if (!email) {
    return null;
  }
  const angularEmailError = Validators.email(new FormControl(email));
  if (angularEmailError) {
    return angularEmailError;
  }
  return EMAIL_WITH_DOMAIN_DOT.test(email) ? null : { email: true };
}

/**
 * Whether the signed-in user may send invitations, looked up in every module
 * of `ModuleService.claims$` (claim values are unique across modules).
 *
 * @param modules The `claims$` value: modules with their `claims`.
 */
export function canSendInvitations(modules: any[] | null): boolean {
  return (modules ?? []).some((module) =>
    INVITE_CLAIMS.some((claim) => module?.claims?.includes(claim))
  );
}

/**
 * Error toast for a failed send/resend: the API's own message when it sends
 * one ("El usuario ya ha sido agregado a la instancia"), else a generic key.
 * A 403 is skipped: the auth interceptor already toasts it.
 */
export function notifyInvitationError(
  notificationService: NotificationService,
  error: HttpErrorResponse
): void {
  if (error?.status === 403) {
    return;
  }
  const body = error?.error;
  const apiMessage =
    typeof body === 'string' ? body : (body?.message ?? body?.Message ?? body?.detail);
  notificationService
    .notifications({ type: 'error', message: apiMessage || 'SEND_INVITATION_ERROR' })
    .subscribe();
}

@Component({
  selector: 'primecontrol-send-invitation',
  templateUrl: './send-invitation.html',
  styleUrls: ['./send-invitation.scss'],
  imports: [
    PRIMECONTROL_BUTTONS,
    PC_DIALOG,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    InputField,
    Select,
    Button,
    ModalBody,
    ModalFooter,
  ],
})
export class SendInvitationComponent {
  public invitationForm: UntypedFormGroup;
  public roleOptions: WritableSignal<{ value: string; label: string }[]> = signal([]);
  /** The request is in flight: SEND is locked so a double click sends once. */
  public isSending: WritableSignal<boolean> = signal(false);

  private _invitationService: InvitationService = inject(InvitationService);
  private _roleService: RoleService = inject(RoleService);
  private _formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
  private _notificationService: NotificationService = inject(NotificationService);

  constructor(
    @Inject('DATA') public _data: any,
    @Inject('CLOSE_DIALOG') private closeModal: (data: any) => void,
    @Inject('TITLE') private assignTitle: (title: string) => void
  ) {
    this.invitationForm = this._formBuilder.group({
      email: [null, [Validators.required, invitationEmailValidator]],
      roleId: [null, [Validators.required]],
    });
    this.assignTitle('SEND_INVITATION');
    this.loadRoles();
  }

  //===========================================================
  //? @PUBLIC METHODS
  //===========================================================

  sendInvitation() {
    if (this.isSending() || this.invitationForm.invalid) {
      return;
    }
    const { email, roleId } = this.invitationForm.getRawValue();
    this.isSending.set(true);
    this._invitationService
      .sendInvitation(this._data.id, normalizeInvitationEmail(email), roleId)
      .subscribe({
        next: (response) => {
          this.isSending.set(false);
          this._notificationService
            .notifications({ type: 'success', message: 'SEND_INVITATION_SUCCESSFULLY' })
            .subscribe();
          this.close(response);
        },
        error: (error) => {
          this.isSending.set(false);
          notifyInvitationError(this._notificationService, error);
        },
      });
  }

  close(data: any = null) {
    this.closeModal(data);
  }

  //===========================================================
  //? @PRIVATE METHODS
  //===========================================================

  private loadRoles() {
    this._roleService.getRolesByInstanceId({ instanceId: this._data.id, pageSize: 100 }).subscribe({
      next: (response) => {
        const options = (response?.items ?? []).map((role: any) => ({
          value: role.id,
          label: role.name,
        }));
        this.roleOptions.set(options);
      },
      error: () => {
        this._notificationService
          .notifications({ type: 'error', message: 'LOAD_ROLES_ERROR' })
          .subscribe();
      },
    });
  }
}
